import { supabase } from '../lib/supabase';
import { emailService } from './emailService';

// Helper to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toCamelCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    acc[camelKey] = toCamelCase(obj?.[key]);
    return acc;
  }, {});
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj?.map(toSnakeCase);
  
  return Object.keys(obj)?.reduce((acc, key) => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    acc[snakeKey] = toSnakeCase(obj?.[key]);
    return acc;
  }, {});
}

export const stripeConnectService = {
  // Create Stripe Connect account
  async createConnectAccount(email) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.functions?.invoke('create-stripe-connect-account', {
        body: { email, businessType: 'individual' }
      });

      if (error) {
        let errorMessage = 'Failed to create Stripe account';
        try {
          if (error?.context?.json) {
            const body = await error?.context?.json();
            errorMessage = body?.error || error?.message || errorMessage;
          } else if (error?.message) {
            errorMessage = error?.message;
          }
        } catch (_) {
          errorMessage = error?.message || errorMessage;
        }
        throw new Error(errorMessage);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return {
        accountId: data?.accountId,
        onboardingUrl: data?.onboardingUrl,
        account: toCamelCase(data?.account),
        existing: data?.existing || false,
      };
    } catch (error) {
      console.error('Create connect account error:', error);
      throw error;
    }
  },

  // Get Stripe Connect account status
  async getConnectStatus() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.functions?.invoke('get-stripe-connect-status');

      if (error) {
        let errorMessage = 'Failed to get account status';
        try {
          if (error?.context?.json) {
            const body = await error?.context?.json();
            errorMessage = body?.error || error?.message || errorMessage;
          } else if (error?.message) {
            errorMessage = error?.message;
          }
        } catch (_) {
          errorMessage = error?.message || errorMessage;
        }
        throw new Error(errorMessage);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return {
        connected: data?.connected,
        account: data?.account ? toCamelCase(data?.account) : null
      };
    } catch (error) {
      console.error('Get connect status error:', error);
      throw error;
    }
  },

  // Get onboarding link for incomplete accounts
  async getOnboardingLink() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.functions?.invoke('create-stripe-onboarding-link');

      if (error) {
        let errorMessage = 'Failed to get onboarding link';
        try {
          if (error?.context?.json) {
            const body = await error?.context?.json();
            errorMessage = body?.error || error?.message || errorMessage;
          } else if (error?.message) {
            errorMessage = error?.message;
          }
        } catch (_) {
          errorMessage = error?.message || errorMessage;
        }
        throw new Error(errorMessage);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      return data?.onboardingUrl;
    } catch (error) {
      console.error('Get onboarding link error:', error);
      throw error;
    }
  },

  // Get payout transactions
  async getPayoutTransactions() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('payout_transactions')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') {
          return [];
        }
        console.log('Get payout transactions error:', error?.message);
        return [];
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get payout transactions error:', error);
      throw error;
    }
  },

  // Get W-9 submission status
  async getW9Status() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.from('stripe_connect_accounts')?.select('w9_submission_status, w9_verification_completed, w9_submission_date, w9_verified_date, is_1099k_eligible, requirements_currently_due, requirements_eventually_due, requirements_past_due')?.eq('user_id', user?.id)?.single();

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') {
          return null;
        }
        console.log('Get W-9 status error:', error?.message);
        return null;
      }

      return data ? toCamelCase(data) : null;
    } catch (error) {
      console.error('Get W-9 status error:', error);
      throw error;
    }
  },

  // Request payout (owner initiates payout after 48-hour waiting period)
  async requestPayout(payoutId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      // Call database function to validate and update payout status
      const { data, error } = await supabase?.rpc('request_payout', { payout_id: payoutId });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        throw new Error(error.message || 'Failed to request payout');
      }

      // RPC returns a JSON object — handle both direct object and wrapped response
      const result = data && typeof data === 'object' ? data : {};
      
      if (result?.success === false) {
        throw new Error(result?.error || 'Failed to request payout');
      }

      // If success is true or result has payout_id, consider it successful
      if (result?.success === true || result?.payout_id) {
        return result;
      }

      // Fallback: if no explicit success/failure, assume success (payout was updated)
      return { success: true, payout_id: payoutId };
    } catch (error) {
      console.error('Request payout error:', error);
      throw error;
    }
  },

  // Process payout (triggers Stripe transfer)
  async processPayout(payoutId) {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase?.functions?.invoke('process-payout', {
        body: { payoutId }
      });

      if (error) {
        // Try to extract real error message from edge function response
        let errorMessage = 'Failed to process payout';
        try {
          if (error?.context) {
            const body = typeof error?.context?.json === 'function'
              ? await error?.context?.json()
              : error?.context;
            errorMessage = body?.error || error?.message || errorMessage;
          } else if (error?.message) {
            errorMessage = error?.message;
          }
        } catch (_) {
          errorMessage = error?.message || errorMessage;
        }
        throw new Error(errorMessage);
      }

      if (data?.error) {
        throw new Error(data.error);
      }

      // Fetch payout details for email notification
      // Use user_profiles joined via user_id on payout_transactions
      const { data: payoutDetails, error: detailsError } = await supabase
        ?.from('payout_transactions')
        ?.select(`
          *,
          bookings(
            start_date,
            end_date,
            booking_reference,
            trailer_listings(title)
          )
        `)
        ?.eq('id', payoutId)
        ?.single();

      // Fetch owner profile separately to avoid FK ambiguity
      const { data: ownerProfile } = await supabase
        ?.from('user_profiles')
        ?.select('full_name, email')
        ?.eq('id', user?.id)
        ?.single();

      if (!detailsError && payoutDetails && ownerProfile) {
        const camelDetails = toCamelCase(payoutDetails);
        const camelProfile = toCamelCase(ownerProfile);
        
        // Send payout confirmation email
        await emailService?.sendPayoutConfirmation({
          recipientEmail: camelProfile?.email,
          recipientName: camelProfile?.fullName,
          payoutData: {
            id: camelDetails?.id,
            grossAmount: camelDetails?.grossAmount,
            platformFeeAmount: camelDetails?.platformFeeAmount,
            netAmount: camelDetails?.netAmount,
            stripeTransferId: data?.transfer_id || camelDetails?.stripeTransferId,
            payoutDate: camelDetails?.payoutDate || new Date()?.toISOString(),
            accountLast4: '****'
          },
          bookingData: {
            startDate: camelDetails?.bookings?.startDate,
            endDate: camelDetails?.bookings?.endDate,
            bookingReference: camelDetails?.bookings?.bookingReference
          },
          trailerData: {
            title: camelDetails?.bookings?.trailerListings?.title
          }
        });
      }

      return data;
    } catch (error) {
      console.error('Process payout error:', error);
      throw error;
    }
  },

  // Get eligible payouts (payouts that can be requested)
  async getEligiblePayouts() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.from('payout_transactions')
        ?.select('*, bookings(end_date, start_date, trailer_listings(title))')
        ?.eq('user_id', user?.id)
        ?.eq('payout_status', 'pending')
        ?.lte('eligible_for_payout_at', new Date()?.toISOString())
        ?.order('eligible_for_payout_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') {
          return [];
        }
        console.log('Get eligible payouts error:', error?.message);
        return [];
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get eligible payouts error:', error);
      throw error;
    }
  },

  // Get pending payouts (not yet eligible)
  async getPendingPayouts() {
    try {
      const { data: { user }, error: authError } = await supabase?.auth?.getUser();
      
      if (authError || !user) {
        throw new Error('User not authenticated');
      }

      const { data, error } = await supabase
        ?.from('payout_transactions')
        ?.select('*, bookings(end_date, start_date, trailer_listings(title))')
        ?.eq('user_id', user?.id)
        ?.eq('payout_status', 'pending')
        ?.gt('eligible_for_payout_at', new Date()?.toISOString())
        ?.order('eligible_for_payout_at', { ascending: true });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error:', error?.message);
          throw error;
        }
        if (error?.code === 'PGRST116') {
          return [];
        }
        console.log('Get pending payouts error:', error?.message);
        return [];
      }

      return data ? data?.map(toCamelCase) : [];
    } catch (error) {
      console.error('Get pending payouts error:', error);
      throw error;
    }
  }
};